import React, { useState } from "react";
import { CheckIcon, ClipboardDocumentIcon } from "@heroicons/react/24/outline";
import { NOTIFICATION } from "@via/context/notifications-context.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { classNames } from "@via/utils/helpers.tsx";

interface CopyableTextProps {
  value: string;
  isTruncated?: boolean;
  className?: string;
  /**
   * Visual variant. Defaults to the current indigo "default" styling.
   * Use "danger" for red-themed actions (e.g., inside error/failed banners).
   */
  variant?: "default" | "danger";
  /**
   * Render only the copy button (no value pill). Useful when the surrounding UI
   * already shows the value elsewhere (e.g., list items) but still wants a copy CTA.
   */
  showValue?: boolean;
  /**
   * Optional label for the button. If omitted, the button shows an icon.
   */
  buttonLabel?: string;
}

export const CopyableText: React.FC<CopyableTextProps> = ({
  value,
  isTruncated,
  className,
  variant = "default",
  showValue = true,
  buttonLabel,
}) => {
  const { setNotification } = useNotifications();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (navigator?.clipboard) {
      try {
        await navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error("Failed to copy: ", err);
      }
    } else {
      setNotification({
        id: "copy-clipboard-not-supported-error",
        message: "Clipboard API",
        details:
          "Clipboard API not supported in this browser. Please copy the text manually or try another browser.",
        variant: NOTIFICATION.ERROR,
      });
    }
  };

  const buttonClasses =
    variant === "danger"
      ? "rounded-md bg-white px-2 py-1 text-xs font-semibold text-red-800 shadow-sm ring-1 ring-inset ring-red-200 hover:bg-red-100"
      : "flex-shrink-0 flex items-center gap-1 px-2 py-1 text-xs font-medium text-indigo-600 hover:text-indigo-700 bg-white border border-indigo-600 rounded-md shadow-sm hover:bg-indigo-100 transition";

  const valueClasses =
    variant === "danger"
      ? "flex-grow bg-white/70 border border-red-200 rounded-md text-red-900 text-sm font-mono px-2 py-1"
      : "flex-grow bg-gray-100 border border-gray-300 rounded-md text-gray-800 text-sm font-mono px-2 py-1";

  return (
    <div
      className={classNames(
        "flex items-center gap-2 max-w-full",
        "hover:cursor-pointer",
        className || ""
      )}
      onClick={handleCopy}>
      {showValue ? (
        <span
          className={classNames(
            valueClasses,
            isTruncated
              ? "truncate max-w-[350px]"
              : "whitespace-normal break-words max-w-[500px]"
          )}
          title={value}>
          {value}
        </span>
      ) : null}

      <button
        className={buttonClasses}
        aria-label="Copy Value"
        type="button"
        onClick={e => {
          // Prevent double-trigger (container click)
          e.stopPropagation();
          handleCopy();
        }}>
        {buttonLabel ? (
          buttonLabel
        ) : copied ? (
          <CheckIcon className="w-5 h-5 text-green-500" />
        ) : (
          <ClipboardDocumentIcon className="w-5 h-5" />
        )}
      </button>
    </div>
  );
};
